import { db } from './firebase';
import { 
  collection, 
  addDoc, 
  doc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  updateDoc, 
  arrayUnion, 
  arrayRemove, 
  serverTimestamp,
  deleteDoc,
  setDoc,
  writeBatch,
  runTransaction
} from 'firebase/firestore';
import Logger from '../../utils/logger';

// Send a friend request
export const sendFriendRequest = async (senderId, receiverId) => {
  try {
    // Validate parameters
    if (!senderId || !receiverId) {
      console.error('Friend request failed: Missing sender or receiver ID', { senderId, receiverId });
      return { success: false, message: 'Missing sender or receiver ID' };
    }

    console.log('Sending friend request from', senderId, 'to', receiverId);
    
    // Instead of creating a new document in a separate collection,
    // we'll update the receiver's user document with a friendRequests array
    // This approach works better with standard Firebase security rules
    // where users can only modify their own documents
    
    try {
      // First verify both users exist
      const senderRef = doc(db, 'users', senderId);
      const receiverRef = doc(db, 'users', receiverId);
      
      const senderDoc = await getDoc(senderRef);
      if (!senderDoc.exists()) {
        console.error('Sender profile not found for ID:', senderId);
        return { success: false, message: 'Sender profile not found' };
      }
      
      const receiverDoc = await getDoc(receiverRef);
      if (!receiverDoc.exists()) {
        console.error('Receiver profile not found for ID:', receiverId);
        return { success: false, message: 'User not found' };
      }
      
      const senderProfile = senderDoc.data();
      const receiverProfile = receiverDoc.data();
      console.log('Retrieved sender profile:', senderProfile.displayName);
      console.log('Retrieved receiver profile:', receiverProfile.displayName);
      
      // Check if request already exists in receiver's document
      const receiverData = receiverDoc.data();
      const existingRequests = receiverData.friendRequests || [];
      
      const alreadySent = existingRequests.some(request => 
        request.senderId === senderId && request.status === 'pending'
      );
      
      if (alreadySent) {
        console.log('Friend request already sent');
        return { success: false, message: 'Friend request already sent' };
      }
      
      // Generate a unique requestId
      const requestId = `${senderId}_${Date.now()}`;
      console.log('Generated requestId:', requestId);
      
      // Create new request object
      const newRequest = {
        senderId,
        senderName: senderProfile.displayName,
        senderPhoto: senderProfile.photoURL || null,
        status: 'pending',
        createdAt: new Date().toISOString(),
        requestId: requestId
      };
      
      // Update the receiver's document with the new request
      await updateDoc(receiverRef, {
        friendRequests: arrayUnion(newRequest),
        hasNewNotification: true
      });
      
      console.log('Friend request added to receiver document');
      
      // Create the sent request object
      const sentRequest = {
        receiverId,
        receiverName: receiverProfile.displayName,
        receiverPhoto: receiverProfile.photoURL || null,
        status: 'pending',
        createdAt: new Date().toISOString(),
        requestId: requestId
      };
      
      console.log('Adding sent request to sender:', sentRequest);
      
      // Also add to sender's sentRequests for tracking
      await updateDoc(senderRef, {
        sentFriendRequests: arrayUnion(sentRequest)
      });
      
      console.log('Friend request successfully sent and added to sentFriendRequests');
      return { success: true, message: 'Friend request sent' };
      
    } catch (error) {
      console.error('Error updating user documents:', error);
      return { success: false, message: `Failed to create friend request: ${error.message}` };
    }
    
  } catch (error) {
    console.error('Error sending friend request:', error);
    return { success: false, message: `Error sending friend request: ${error.message}` };
  }
};

// Accept a friend request
export const acceptFriendRequest = async (requestId, currentUserId, senderId) => {
  try {
    console.log(`Accepting friend request ${requestId} from ${senderId} to ${currentUserId}`);
    
    // Get current user document
    const currentUserRef = doc(db, 'users', currentUserId);
    const currentUserDoc = await getDoc(currentUserRef);
    
    if (!currentUserDoc.exists()) {
      console.error('Current user profile not found');
      return { success: false, message: 'Your profile not found' };
    }
    
    const currentUserData = currentUserDoc.data();
    const friendRequests = currentUserData.friendRequests || [];
    
    // Find the request
    const requestIndex = friendRequests.findIndex(req => req.requestId === requestId);
    if (requestIndex === -1) {
      console.error(`Request ${requestId} not found in user's requests`);
      return { success: false, message: 'Friend request not found' };
    }
    
    const request = friendRequests[requestIndex];
    console.log('Found request:', request);
    
    // Make sure we're using the correct sender ID - either from the request or from the parameter
    const actualSenderId = senderId || request.senderId;
    
    // Get sender document
    const senderRef = doc(db, 'users', actualSenderId);
    const senderDoc = await getDoc(senderRef);
    
    if (!senderDoc.exists()) {
      console.error(`Sender profile not found for ID: ${actualSenderId}`);
      return { success: false, message: 'Sender profile not found' };
    }
    
    const senderData = senderDoc.data();
    console.log('Retrieved sender profile:', senderData.displayName);
    
    // IMPORTANT CHANGE: Instead of updating the request status, we'll completely remove it
    // Remove the request from current user's friendRequests array
    const updatedRequests = friendRequests.filter(req => req.requestId !== requestId);
    
    console.log('Removing friend request instead of updating status');
    
    // Make sure the friends arrays are properly initialized
    const currentUserFriends = Array.isArray(currentUserData.friends) ? currentUserData.friends : [];
    const senderFriends = Array.isArray(senderData.friends) ? senderData.friends : [];
    
    console.log('Current user friends array:', currentUserFriends.length);
    console.log('Sender friends array:', senderFriends.length);
    
    // Check if already friends
    const alreadyFriends = currentUserFriends.some(friend => friend.id === actualSenderId);
    console.log('Already friends?', alreadyFriends);
    
    if (!alreadyFriends) {
      console.log('Adding friend to current user friends array');
      // Add friend to current user's friends array
      const friendToAdd = {
        id: actualSenderId,
        displayName: request.senderName || senderData.displayName,
        photoURL: request.senderPhoto || senderData.photoURL || null,
        addedAt: new Date().toISOString()
      };
      
      console.log('Adding friend to current user:', friendToAdd);
      
      // Define the currentUserFriendsUpdated first to calculate the right count
      const currentUserFriendsUpdated = [...currentUserFriends, friendToAdd];
      
      // Update current user's document with the new friend
      await updateDoc(currentUserRef, {
        friendRequests: updatedRequests, // Use the filtered array without the accepted request
        friends: currentUserFriendsUpdated,
        friendCount: currentUserFriendsUpdated.length
      });
      
      console.log('Current user document updated with new friend');
      
      // Add current user to sender's friends array
      const senderFriend = {
        id: currentUserId,
        displayName: currentUserData.displayName,
        photoURL: currentUserData.photoURL || null,
        addedAt: new Date().toISOString()
      };
      
      console.log('Adding current user to sender friends:', senderFriend);
      
      // Define the senderFriendsUpdated first to calculate the right count
      const senderFriendsUpdated = [...senderFriends, senderFriend];
      
      // Get sent requests from sender's document
      const senderSentRequests = senderData.sentFriendRequests || [];
      
      // Remove the request from sender's sentFriendRequests array
      const updatedSentRequests = senderSentRequests.filter(req => req.requestId !== requestId);
      
      // Create a "friend request accepted" notification for the original sender
      const acceptedNotification = {
        id: `accepted_${requestId}_${Date.now()}`,
        type: 'friendRequestAccepted',
        message: `${currentUserData.displayName} accepted your friend request`,
        senderId: currentUserId,
        senderName: currentUserData.displayName,
        senderPhoto: currentUserData.photoURL || null,
        read: false,
        createdAt: new Date().toISOString()
      };
      
      console.log('Creating friend request accepted notification:', acceptedNotification);
      
      // Get existing notifications array from sender's document
      const senderNotifications = senderData.notifications || [];
      console.log(`Sender currently has ${senderNotifications.length} notifications`);
      
      // Update sender's document
      await updateDoc(senderRef, {
        friends: senderFriendsUpdated,
        friendCount: senderFriendsUpdated.length,
        sentFriendRequests: updatedSentRequests,
        notifications: [...senderNotifications, acceptedNotification],
        hasNewNotification: true
      });
      
      console.log('Successfully added friend request accepted notification to sender document');
      
      console.log('Sender document updated with new friend and request removed');
    } else {
      // Just remove the request if already friends
      await updateDoc(currentUserRef, {
        friendRequests: updatedRequests
      });
      
      // Also remove from sender's sent requests
      const senderSentRequests = senderData.sentFriendRequests || [];
      const updatedSentRequests = senderSentRequests.filter(req => req.requestId !== requestId);
      
      await updateDoc(senderRef, {
        sentFriendRequests: updatedSentRequests
      });
      
      console.log('Already friends, removed request from both users');
    }
    
    // Trigger the refreshFriendsList function if it exists
    // This will ensure the UI updates properly
    setTimeout(() => {
      if (window && window.refreshFriendsList) {
        console.log('Triggering friends list refresh');
        window.refreshFriendsList();
      }
    }, 1000);
    
    console.log('Friend request successfully accepted and removed');
    return { success: true, message: 'Friend request accepted' };
    
  } catch (error) {
    console.error('Error accepting friend request:', error);
    return { success: false, message: `Error accepting friend request: ${error.message}` };
  }
};

// Decline a friend request
export const declineFriendRequest = async (requestId, currentUserId) => {
  try {
    // Get current user document
    const currentUserRef = doc(db, 'users', currentUserId);
    const currentUserDoc = await getDoc(currentUserRef);
    
    if (!currentUserDoc.exists()) {
      return { success: false, message: 'Your profile not found' };
    }
    
    const currentUserData = currentUserDoc.data();
    const friendRequests = currentUserData.friendRequests || [];
    
    // Find the request
    const requestIndex = friendRequests.findIndex(req => req.requestId === requestId);
    if (requestIndex === -1) {
      return { success: false, message: 'Friend request not found' };
    }
    
    const request = friendRequests[requestIndex];
    
    // Remove the request from current user's document
    const updatedRequests = friendRequests.filter(req => req.requestId !== requestId);
    
    await updateDoc(currentUserRef, {
      friendRequests: updatedRequests
    });
    
    // Update sender's sentFriendRequests - COMPLETELY REMOVE the request 
    // so the sender can send a new request later
    const senderRef = doc(db, 'users', request.senderId);
    const senderDoc = await getDoc(senderRef);
    
    if (senderDoc.exists()) {
      const senderData = senderDoc.data();
      const sentRequests = senderData.sentFriendRequests || [];
      
      // Remove the request completely from sender's list
      const updatedSentRequests = sentRequests.filter(req => req.requestId !== requestId);
      
      await updateDoc(senderRef, {
        sentFriendRequests: updatedSentRequests
      });
      
      console.log(`Friend request ${requestId} completely removed from sender's record`);
    }
    
    return { success: true, message: 'Friend request declined' };
    
  } catch (error) {
    console.error('Error declining friend request:', error);
    return { success: false, message: `Error declining friend request: ${error.message}` };
  }
};

// Get pending friend requests for a user
export const getPendingFriendRequests = async (userId) => {
  try {
    // Get the user document
    const userDoc = await getDoc(doc(db, 'users', userId));
    
    if (!userDoc.exists()) {
      console.error('User profile not found');
      return [];
    }
    
    const userData = userDoc.data();
    const friendRequests = userData.friendRequests || [];
    
    // Filter for pending requests
    const pendingRequests = friendRequests.filter(request => 
      request.status === 'pending'
    );
    
    // Sort by creation date (newest first)
    return pendingRequests.sort((a, b) => {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);
      return dateB - dateA;
    });
    
  } catch (error) {
    console.error('Error getting pending friend requests:', error);
    return [];
  }
};

// Get sent friend requests for a user
export const getSentFriendRequests = async (userId, includeAccepted = false) => {
  try {
    console.log('Getting sent friend requests for user:', userId, 'includeAccepted:', includeAccepted);
    
    // Get the user document
    const userDoc = await getDoc(doc(db, 'users', userId));
    
    if (!userDoc.exists()) {
      console.error('User profile not found');
      return [];
    }
    
    const userData = userDoc.data();
    const sentRequests = userData.sentFriendRequests || [];
    
    console.log(`Found ${sentRequests.length} total sent requests for user ${userId}`);
    
    if (sentRequests.length > 0) {
      // Log status breakdown
      const pendingCount = sentRequests.filter(req => req.status === 'pending').length;
      const acceptedCount = sentRequests.filter(req => req.status === 'accepted').length;
      const otherCount = sentRequests.length - pendingCount - acceptedCount;
      
      console.log(`Sent requests status - Pending: ${pendingCount}, Accepted: ${acceptedCount}, Other: ${otherCount}`);
      
      if (sentRequests.length <= 5) {
        // Log all requests if there are not too many
        sentRequests.forEach((req, index) => {
          console.log(`Sent request #${index + 1}:`, req.requestId, req.receiverName, req.status);
        });
      } else {
        // Just log a sample
        console.log('First sent request sample:', sentRequests[0]);
      }
    }
    
    // Filter based on includeAccepted parameter
    let filteredRequests = sentRequests;
    if (!includeAccepted) {
      // Only include pending requests
      filteredRequests = sentRequests.filter(request => 
        request.status === 'pending'
      );
    }
    
    console.log(`After filtering (includeAccepted=${includeAccepted}), returning ${filteredRequests.length} sent requests`);
    
    // Sort by creation date (newest first)
    return filteredRequests.sort((a, b) => {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);
      return dateB - dateA;
    });
    
  } catch (error) {
    console.error('Error getting sent friend requests:', error);
    return [];
  }
};

// Get all notifications for a user
export const getUserNotifications = async (userId) => {
  try {
    console.log('🔍 getUserNotifications called for userId:', userId);
    
    // Get the user document
    const userDoc = await getDoc(doc(db, 'users', userId));
    
    if (!userDoc.exists()) {
      console.error('User profile not found');
      return [];
    }
    
    const userData = userDoc.data();
    console.log('📄 User document data keys:', Object.keys(userData));
    console.log('📄 User notifications array:', userData.notifications);
    console.log('📄 User friendRequests array:', userData.friendRequests);
    
    // Get friend request notifications (incoming requests)
    const friendRequests = (userData.friendRequests || [])
      .filter(request => request.status === 'pending')
      .map(request => ({
        id: request.requestId,
        type: 'friendRequest',
        senderId: request.senderId,
        senderName: request.senderName,
        senderPhoto: request.senderPhoto,
        message: `${request.senderName} sent you a friend request`,
        read: false,
        createdAt: request.createdAt,
        requestId: request.requestId
      }));
    
    console.log('✉️ Mapped friend requests:', friendRequests);
    
    // Get other notifications (including friend request accepted)
    // Handle case where notifications might be an object instead of array
    let notificationsArray = [];
    if (userData.notifications) {
      if (Array.isArray(userData.notifications)) {
        notificationsArray = userData.notifications;
      } else if (typeof userData.notifications === 'object') {
        // Convert object to array if it's an object
        notificationsArray = Object.values(userData.notifications);
      }
    }
    
    const otherNotifications = notificationsArray
      .filter(notification => notification && !notification.read)
      .map(notification => ({
        id: notification.id,
        type: notification.type,
        senderId: notification.senderId,
        senderName: notification.senderName,
        senderPhoto: notification.senderPhoto,
        message: notification.message,
        read: notification.read,
        createdAt: notification.createdAt
      }));
    
    console.log('🔔 Mapped other notifications:', otherNotifications);
    
    // Filter out friend request accepted notifications for debugging
    const acceptedNotifs = otherNotifications.filter(n => n.type === 'friendRequestAccepted');
    console.log('🎉 Friend request accepted notifications found:', acceptedNotifs);
    
    // Combine all notifications
    const allNotifications = [...friendRequests, ...otherNotifications];
    console.log('📋 Combined all notifications:', allNotifications);
    
    // Reset the notification flag after a small delay to allow UI to process
    if (userData.hasNewNotification) {
      setTimeout(async () => {
        try {
          await updateDoc(doc(db, 'users', userId), {
            hasNewNotification: false
          });
        } catch (error) {
          console.error('Error resetting notification flag:', error);
        }
      }, 1000); // 1 second delay
    }
    
    // Sort by creation date (newest first)
    const sortedNotifications = allNotifications.sort((a, b) => {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);
      return dateB - dateA;
    });
    
    console.log('📤 Returning sorted notifications:', sortedNotifications);
    return sortedNotifications;
  } catch (error) {
    console.error('Error getting user notifications:', error);
    return [];
  }
};

// Clean up stale friend requests for a user
export const cleanupFriendRequests = async (userId) => {
  try {
    console.log('Cleaning up stale friend requests for user:', userId);
    
    // Get the current user document
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      console.error('User profile not found');
      return { success: false, message: 'User profile not found' };
    }
    
    const userData = userDoc.data();
    const sentRequests = userData.sentFriendRequests || [];
    const userFriends = userData.friends || [];
    let requestsChanged = false;
    let friendsChanged = false;
    
    // Check each sent request to see if it still exists in the receiver's document
    const validatedRequests = [];
    
    for (const request of sentRequests) {
      // If it's already accepted, keep it 
      if (request.status === 'accepted') {
        // Double-check that this person is actually in the friends list
        const isFriend = userFriends.some(friend => friend.id === request.receiverId);
        
        if (isFriend) {
          // Everything is consistent, keep the request
          validatedRequests.push(request);
        } else {
          // Request is accepted but person isn't in friends list - inconsistent state
          console.log(`Found inconsistency: Request to ${request.receiverName} is accepted but not in friends list`);
          
          // Check if the receiver has the current user as a friend
          try {
            const receiverDoc = await getDoc(doc(db, 'users', request.receiverId));
            if (receiverDoc.exists()) {
              const receiverData = receiverDoc.data();
              const receiverFriends = receiverData.friends || [];
              
              if (receiverFriends.some(friend => friend.id === userId)) {
                // Receiver has the user as a friend, so add the receiver to user's friends list
                console.log('Fixing inconsistency: Adding missing friend to user\'s friend list');
                
                const missingFriend = {
                  id: request.receiverId,
                  displayName: request.receiverName,
                  photoURL: request.receiverPhoto,
                  addedAt: new Date().toISOString()
                };
                
                userFriends.push(missingFriend);
                friendsChanged = true;
                
                // Keep the accepted request
                validatedRequests.push(request);
              } else {
                // Receiver doesn't have the user as a friend, so mark request as pending
                console.log('Marking inconsistent accepted request as pending again');
                validatedRequests.push({
                  ...request,
                  status: 'pending'
                });
                requestsChanged = true;
              }
            } else {
              // Receiver doesn't exist, keep the request as is
              validatedRequests.push(request);
            }
          } catch (error) {
            console.error('Error checking receiver for friend relationship:', error);
            validatedRequests.push(request);
          }
        }
        continue;
      }
      
      // Handle pending requests
      // Check if this request still exists in the receiver's document
      try {
        const receiverRef = doc(db, 'users', request.receiverId);
        const receiverDoc = await getDoc(receiverRef);
        
        if (receiverDoc.exists()) {
          const receiverData = receiverDoc.data();
          const receiverRequests = receiverData.friendRequests || [];
          const receiverFriends = receiverData.friends || [];
          
          // Look for a matching request in the receiver's requests
          const pendingRequest = receiverRequests.find(
            req => req.requestId === request.requestId
          );
          
          // Check if they are already friends
          const alreadyFriends = receiverFriends.some(friend => friend.id === userId);
          
          if (pendingRequest) {
            // Request exists
            if (pendingRequest.status === 'accepted' || alreadyFriends) {
              // Request was accepted, update our copy to reflect this
              validatedRequests.push({
                ...request,
                status: 'accepted'
              });
              
              // If not already friends, add receiver to friends list
              if (alreadyFriends && !userFriends.some(friend => friend.id === request.receiverId)) {
                const newFriend = {
                  id: request.receiverId,
                  displayName: request.receiverName,
                  photoURL: request.receiverPhoto,
                  addedAt: new Date().toISOString()
                };
                
                userFriends.push(newFriend);
                friendsChanged = true;
                console.log('Added missing friend to local user\'s friends list');
              }
              
              requestsChanged = true;
              console.log('Updating request status to accepted:', request.requestId);
            } else if (pendingRequest.status === 'pending') {
              // Request is still pending
              validatedRequests.push(request);
            } else {
              // Request was declined or invalid
              console.log('Removing declined request to:', request.receiverId);
              requestsChanged = true;
            }
          } else if (alreadyFriends) {
            // The request is no longer in the receiver's document, but they are friends
            // This means the request was accepted and possibly cleaned up
            validatedRequests.push({
              ...request,
              status: 'accepted'
            });
            
            // Make sure the user has this person in their friends list
            if (!userFriends.some(friend => friend.id === request.receiverId)) {
              const newFriend = {
                id: request.receiverId,
                displayName: request.receiverName,
                photoURL: request.receiverPhoto,
                addedAt: new Date().toISOString()
              };
              
              userFriends.push(newFriend);
              friendsChanged = true;
              console.log('Added missing friend to local user\'s friends list');
            }
            
            requestsChanged = true;
            console.log('Request is gone but users are friends, marking as accepted');
          } else {
            // Request was declined or doesn't exist
            console.log('Removing stale request to:', request.receiverId);
            requestsChanged = true;
          }
        } else {
          // Receiver doesn't exist, keep the request
          validatedRequests.push(request);
        }
      } catch (error) {
        console.error('Error checking receiver:', error);
        // In case of error, keep the request
        validatedRequests.push(request);
      }
    }
    
    // Update the user document if changes were made
    if (requestsChanged || friendsChanged) {
      try {
        const updateData = {};
        
        if (requestsChanged) {
          updateData.sentFriendRequests = validatedRequests;
          console.log('Updating sent friend requests with validated data');
        }
        
        if (friendsChanged) {
          updateData.friends = userFriends;
          updateData.friendCount = userFriends.length;
          console.log('Updating friends list with fixed data');
        }
        
        // Update the user document with all changes
        await updateDoc(userRef, updateData);
        
        console.log('Friend request cleanup complete with changes applied');
        return { 
          success: true, 
          message: 'Friend request synchronization complete'
        };
      } catch (updateError) {
        console.error('Error updating user document during cleanup:', updateError);
        return { success: false, message: `Error updating: ${updateError.message}` };
      }
    }
    
    console.log('Friend request cleanup complete - no changes needed');
    return { success: true, message: 'No synchronization issues found' };
    
  } catch (error) {
    console.error('Error cleaning up friend requests:', error);
    return { success: false, message: `Error cleaning up requests: ${error.message}` };
  }
};

// Get a user's friends list
export const getUserFriends = async (userId) => {
  try {
    Logger.debug('Getting friends for user:', userId);
    
    if (!userId) {
      Logger.error('Cannot get friends: Missing user ID');
      return { success: false, friends: [], message: 'Missing user ID' };
    }

    // Get the user document
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      console.error('User profile not found for ID:', userId);
      return { success: false, friends: [], message: 'User profile not found' };
    }
    
    const userData = userDoc.data();
    Logger.debug('User data retrieved:', userData.displayName);
    
    // Detailed logging of the user document
    Logger.debug('Full user data keys:', Object.keys(userData));
    
    // Check if friends field exists at all
    if (!userData.hasOwnProperty('friends')) {
      Logger.warn('User does not have a friends field in their document');
      
      // Initialize an empty friends array for the user
      await updateDoc(userRef, {
        friends: [],
        friendCount: 0
      });
      
      Logger.info('Created empty friends array for user');
      return { 
        success: true, 
        friends: [],
        message: 'No friends found, created empty friends array'
      };
    }
    
    const friends = userData.friends || [];
    Logger.debug(`Raw friends array (${friends.length} items):`, JSON.stringify(friends));
    
    // Validate each friend entry has required fields
    const validFriends = friends.filter(friend => {
      if (!friend || typeof friend !== 'object') {
        console.warn('Invalid friend entry (not an object):', friend);
        return false;
      }
      
      if (!friend.id || !friend.displayName) {
        console.warn('Friend missing required fields:', friend);
        return false;
      }
      
      return true;
    });
    
    console.log(`Valid friends count: ${validFriends.length} of ${friends.length}`);
    
    // Verify that each friend's user document still exists
    const verifiedFriends = [];
    const missingFriendIds = [];

    // Check existence in parallel with Promise.all (but ignore errors)
    await Promise.all(
      validFriends.map(async (friend) => {
        try {
          const friendDoc = await getDoc(doc(db, 'users', friend.id));
          if (friendDoc.exists()) {
            verifiedFriends.push(friend);
          } else {
            missingFriendIds.push(friend.id);
          }
        } catch (e) {
          console.error('Error verifying friend existence', friend.id, e);
          // Keep friend for now if error
          verifiedFriends.push(friend);
        }
      })
    );

    // If we found missing friends, clean them from Firestore
    if (missingFriendIds.length > 0) {
      console.log(`Removing ${missingFriendIds.length} stale friends for user ${userId}`);
      const cleanedFriends = verifiedFriends;
      try {
        await updateDoc(userRef, {
          friends: cleanedFriends,
          friendCount: cleanedFriends.length,
        });
      } catch (cleanErr) {
        console.error('Failed to clean stale friends list', cleanErr);
      }
    }

    // Sort by display name
    const sortedFriends = [...verifiedFriends].sort((a, b) => a.displayName.localeCompare(b.displayName));
    
    // Update friendCount if it doesn't match
    if (userData.friendCount !== validFriends.length) {
      console.log(`Updating friendCount from ${userData.friendCount} to ${validFriends.length}`);
      await updateDoc(userRef, {
        friendCount: validFriends.length
      });
    }
    
    console.log(`Returning ${sortedFriends.length} sorted friends`);
    return { 
      success: true, 
      friends: sortedFriends,
      message: `Retrieved ${sortedFriends.length} friends`
    };
    
  } catch (error) {
    Logger.error('Error getting user friends:', error);
    return { 
      success: false, 
      friends: [], 
      message: `Error getting friends: ${error.message}` 
    };
  }
};

// Remove a friend
export const removeFriend = async (currentUserId, friendId) => {
  try {
    console.log(`=== REMOVING FRIEND ===`);
    console.log(`Current User: ${currentUserId}`);
    console.log(`Friend to Remove: ${friendId}`);
    
    // Use the sync implementation instead of the regular bidirectional one
    const result = await syncRemoveFriend(currentUserId, friendId);
    
    console.log('Remove Friend Result:', result);
    console.log(`=== FRIEND REMOVAL ${result.success ? 'COMPLETED' : 'FAILED'} ===`);
    
    return result;
  } catch (error) {
    console.error('=== FRIEND REMOVAL ERROR ===', error);
    return { success: false, message: `Unexpected error: ${error.message}` };
  }
};

// Cancel a sent friend request
export const cancelFriendRequest = async (senderId, receiverId, requestId) => {
  try {
    console.log(`Canceling friend request from ${senderId} to ${receiverId}, requestId: ${requestId || 'not provided'}`);
    
    if (!senderId || !receiverId) {
      return { success: false, message: 'Missing sender or receiver ID' };
    }
    
    // Get both user documents
    const senderRef = doc(db, 'users', senderId);
    const receiverRef = doc(db, 'users', receiverId);
    
    const [senderDoc, receiverDoc] = await Promise.all([
      getDoc(senderRef),
      getDoc(receiverRef)
    ]);
    
    if (!senderDoc.exists()) {
      return { success: false, message: 'Sender profile not found' };
    }
    
    if (!receiverDoc.exists()) {
      return { success: false, message: 'Receiver profile not found' };
    }
    
    // Get sent requests from sender's document
    const senderData = senderDoc.data();
    const sentRequests = senderData.sentFriendRequests || [];
    console.log(`Found ${sentRequests.length} sent requests for sender ${senderId}`);
    
    // Find the specific request by receiver ID and status
    let targetRequest;
    if (requestId) {
      targetRequest = sentRequests.find(req => req.requestId === requestId);
      console.log(`Searching by requestId: ${requestId}, found:`, targetRequest ? 'yes' : 'no');
    } else {
      targetRequest = sentRequests.find(req => 
        req.receiverId === receiverId && req.status === 'pending'
      );
      console.log(`Searching by receiverId and status, found:`, targetRequest ? 'yes' : 'no');
    }
    
    if (!targetRequest) {
      console.log('Available sent requests:', JSON.stringify(sentRequests));
      return { success: false, message: 'Friend request not found' };
    }
    
    console.log('Found target request:', targetRequest);
    
    // Update sender's document to remove the request
    const updatedSentRequests = sentRequests.filter(req => 
      req.requestId !== targetRequest.requestId
    );
    
    await updateDoc(senderRef, {
      sentFriendRequests: updatedSentRequests
    });
    console.log('Updated sender document, removed request');
    
    // Update receiver's document to remove the request
    const receiverData = receiverDoc.data();
    const receiverRequests = receiverData.friendRequests || [];
    
    const updatedReceiverRequests = receiverRequests.filter(req => 
      req.requestId !== targetRequest.requestId
    );
    
    await updateDoc(receiverRef, {
      friendRequests: updatedReceiverRequests
    });
    console.log('Updated receiver document, removed request');
    
    return { success: true, message: 'Friend request canceled' };
  } catch (error) {
    console.error('Error canceling friend request:', error);
    return { success: false, message: `Error canceling request: ${error.message}` };
  }
};

// Force remove a friend using direct arrayRemove operation
export const directFriendRemoval = async (currentUserId, friendId) => {
  try {
    if (!currentUserId || !friendId) {
      console.error('Missing user IDs for direct friend removal:', { currentUserId, friendId });
      return { success: false, message: 'Missing user IDs' };
    }

    console.log(`DIRECT removing friend ${friendId} from user ${currentUserId}`);
    
    // Get current user document reference
    const userRef = doc(db, 'users', currentUserId);
    
    // First, get the entire user document to examine the friends array
    const userDoc = await getDoc(userRef);
    if (!userDoc.exists()) {
      console.error(`User document not found for ID: ${currentUserId}`);
      return { success: false, message: 'User not found' };
    }
    
    const userData = userDoc.data();
    const friends = userData.friends || [];
    
    // Check how many friends we have before
    console.log(`User has ${friends.length} friends before removal`);
    
    // Find the specific friend object to remove
    const friendToRemove = friends.find(friend => friend.id === friendId);
    
    if (!friendToRemove) {
      console.error(`Friend ${friendId} not found in user's friends list`);
      return { success: false, message: 'Friend not found in your list' };
    }
    
    console.log('Found friend to remove:', friendToRemove);
    
    // Create a batch for atomicity
    const batch = writeBatch(db);
    
    // Try using a batch write for better atomicity
    // 1. First, remove the specific friend object from the array
    batch.update(userRef, {
      friends: arrayRemove(friendToRemove)
    });
    
    // 2. Then update the count separately
    batch.update(userRef, {
      friendCount: friends.length - 1
    });
    
    // Commit the batch
    await batch.commit();
    console.log('Batch committed successfully');
    
    // Verify the update worked
    const afterDoc = await getDoc(userRef);
    const afterData = afterDoc.data();
    const afterFriends = afterData.friends || [];
    
    console.log(`User now has ${afterFriends.length} friends after removal`);
    
    if (friends.length === afterFriends.length) {
      console.error('Friend count did not change after removal operation');
      return { 
        success: false, 
        message: 'Database operation completed but friend count did not change',
        friendBefore: friendToRemove,
        beforeCount: friends.length,
        afterCount: afterFriends.length
      };
    }
    
    return { 
      success: true, 
      message: 'Friend removed successfully using direct method',
      beforeCount: friends.length,
      afterCount: afterFriends.length
    };
  } catch (error) {
    console.error('Error in direct friend removal:', error);
    return { success: false, message: `Error: ${error.message}` };
  }
};

// Direct friend removal with SET operation instead of arrayRemove
export const setFriendRemoval = async (currentUserId, friendId) => {
  try {
    if (!currentUserId || !friendId) {
      console.error('Missing user IDs for set friend removal:', { currentUserId, friendId });
      return { success: false, message: 'Missing user IDs' };
    }

    console.log(`SET removing friend ${friendId} from user ${currentUserId}`);
    
    // Get current user document reference
    const userRef = doc(db, 'users', currentUserId);
    
    // Get the entire user document
    const userDoc = await getDoc(userRef);
    if (!userDoc.exists()) {
      console.error(`User document not found for ID: ${currentUserId}`);
      return { success: false, message: 'User not found' };
    }
    
    const userData = userDoc.data();
    const friends = userData.friends || [];
    
    // Log details about the operation
    console.log(`User has ${friends.length} friends before removal`);
    console.log('Current friends:', JSON.stringify(friends));
    
    // Check if the friend exists in the array
    const friendIndex = friends.findIndex(friend => friend.id === friendId);
    if (friendIndex === -1) {
      console.error(`Friend ${friendId} not found in user's friends list`);
      return { success: false, message: 'Friend not found in your list' };
    }
    
    console.log(`Found friend at index ${friendIndex}:`, friends[friendIndex]);
    
    // Create a new array WITHOUT the friend to remove
    const updatedFriends = friends.filter(friend => friend.id !== friendId);
    console.log(`Will update with ${updatedFriends.length} friends`);
    
    // Use a direct set operation to replace the entire field
    try {
      // Use a transaction for atomicity
      await updateDoc(userRef, {
        friends: updatedFriends,
        friendCount: updatedFriends.length
      });
      
      console.log('Friend removal update completed');
      
      // Verify the update worked with a fresh read
      const afterDoc = await getDoc(userRef);
      const afterData = afterDoc.data();
      const afterFriends = afterData.friends || [];
      
      console.log(`User now has ${afterFriends.length} friends after removal`);
      console.log('Friends after removal:', JSON.stringify(afterFriends));
      
      if (friends.length === afterFriends.length) {
        console.error('Friend count did not change after SET operation');
        return { 
          success: false, 
          message: 'Database operation appeared to complete but friend count did not change',
          beforeCount: friends.length,
          afterCount: afterFriends.length
        };
      }
      
      return { 
        success: true, 
        message: 'Friend removed successfully using SET method',
        beforeCount: friends.length,
        afterCount: afterFriends.length
      };
    } catch (updateError) {
      console.error('Error in SET removal update:', updateError);
      return { success: false, message: `Update error: ${updateError.message}` };
    }
  } catch (error) {
    console.error('Error in SET friend removal:', error);
    return { success: false, message: `Error: ${error.message}` };
  }
};

// Transaction-based friend removal
export const transactionFriendRemoval = async (currentUserId, friendId) => {
  try {
    if (!currentUserId || !friendId) {
      console.error('Missing user IDs for transaction friend removal:', { currentUserId, friendId });
      return { success: false, message: 'Missing user IDs' };
    }

    console.log(`TRANSACTION removing friend ${friendId} from user ${currentUserId}`);
    
    // Get current user document reference
    const userRef = doc(db, 'users', currentUserId);
    
    // Use a transaction for guaranteed atomicity
    const result = await runTransaction(db, async (transaction) => {
      // First read the user document inside the transaction
      const userDoc = await transaction.get(userRef);
      
      if (!userDoc.exists()) {
        throw new Error(`User document not found for ID: ${currentUserId}`);
      }
      
      const userData = userDoc.data();
      const friends = userData.friends || [];
      
      // Log details about the operation
      console.log(`User has ${friends.length} friends before transaction removal`);
      
      // Check if the friend exists in the array
      const friendExists = friends.some(friend => friend.id === friendId);
      if (!friendExists) {
        throw new Error(`Friend ${friendId} not found in user's friends list`);
      }
      
      // Create a new array WITHOUT the friend to remove
      const updatedFriends = friends.filter(friend => friend.id !== friendId);
      
      // Update the document in the transaction
      transaction.update(userRef, {
        friends: updatedFriends,
        friendCount: updatedFriends.length
      });
      
      // Return the before and after counts for verification
      return {
        beforeCount: friends.length,
        afterCount: updatedFriends.length
      };
    });
    
    console.log('Transaction completed successfully, counts:', result);
    
    // Verify the transaction worked with a fresh read
    const afterDoc = await getDoc(userRef);
    const afterData = afterDoc.data();
    const afterFriends = afterData.friends || [];
    
    console.log(`User now has ${afterFriends.length} friends after transaction removal`);
    
    if (result.beforeCount === afterFriends.length) {
      console.error('Friend count did not change after TRANSACTION');
      return { 
        success: false, 
        message: 'Transaction completed but friend count did not change in database',
        beforeCount: result.beforeCount,
        afterCount: afterFriends.length
      };
    }
    
    return { 
      success: true, 
      message: 'Friend removed successfully using TRANSACTION method',
      beforeCount: result.beforeCount,
      afterCount: afterFriends.length
    };
  } catch (error) {
    console.error('Error in TRANSACTION friend removal:', error);
    return { success: false, message: `Transaction error: ${error.message}` };
  }
};

// Bidirectional Friend Removal - improved version
export const bidirectionalFriendRemoval = async (currentUserId, friendId) => {
  try {
    if (!currentUserId || !friendId) {
      console.error('Missing user IDs for bidirectional friend removal:', { currentUserId, friendId });
      return { success: false, message: 'Missing user IDs' };
    }

    console.log(`BIDIRECTIONAL removing friend relationship between ${currentUserId} and ${friendId}`);
    
    // STEP 1: Get both user documents
    const currentUserRef = doc(db, 'users', currentUserId);
    const friendUserRef = doc(db, 'users', friendId);
    
    const [currentUserDoc, friendUserDoc] = await Promise.all([
      getDoc(currentUserRef),
      getDoc(friendUserRef)
    ]);
    
    if (!currentUserDoc.exists()) {
      console.error(`Current user document not found for ID: ${currentUserId}`);
      return { success: false, message: 'Your profile not found' };
    }
    
    // STEP 2: Update current user's document (this will always work with security rules)
    const currentUserData = currentUserDoc.data();
    const currentUserFriends = currentUserData.friends || [];
    
    // Find the friend to remove in current user's friends list
    const friendInCurrentUsersList = currentUserFriends.find(friend => friend.id === friendId);
    
    if (!friendInCurrentUsersList) {
      console.warn(`Friend ${friendId} not found in current user's friends list - might be one-sided friendship`);
      // Don't return failure here - just log it and try to remove from friend's side anyway
      // This handles cases where there's a one-sided friendship or data inconsistency
    }
    
    // Filter out the friend from current user's friends
    const updatedCurrentUserFriends = currentUserFriends.filter(
      friend => friend.id !== friendId
    );
    
    // STEP 2.1: Check for and remove any lingering friend requests in current user's document
    const currentUserFriendRequests = currentUserData.friendRequests || [];
    const currentUserSentRequests = currentUserData.sentFriendRequests || [];
    
    // Filter out any requests from the friend being removed
    const updatedCurrentUserFriendRequests = currentUserFriendRequests.filter(
      req => req.senderId !== friendId
    );
    
    // Filter out any requests to the friend being removed
    const updatedCurrentUserSentRequests = currentUserSentRequests.filter(
      req => req.receiverId !== friendId
    );
    
    // Check if any changes were made
    const friendsChanged = updatedCurrentUserFriends.length !== currentUserFriends.length;
    const requestsChanged = 
      updatedCurrentUserFriendRequests.length !== currentUserFriendRequests.length ||
      updatedCurrentUserSentRequests.length !== currentUserSentRequests.length;
    
    if (requestsChanged) {
      console.log(`Removing lingering friend requests between ${currentUserId} and ${friendId}`);
    }
    
    // Only update if there are actual changes to make
    if (friendsChanged || requestsChanged) {
      await updateDoc(currentUserRef, {
        friends: updatedCurrentUserFriends,
        friendCount: updatedCurrentUserFriends.length,
        friendRequests: updatedCurrentUserFriendRequests,
        sentFriendRequests: updatedCurrentUserSentRequests
      });
      
      console.log(`Updated current user's document to remove friend: ${friendId}`);
    } else {
      console.log(`No changes needed for current user's document - friend ${friendId} not found in any lists`);
    }
    
    // STEP 3: Check if we have the friend's document
    if (!friendUserDoc.exists()) {
      console.warn(`Friend's document not found: ${friendId}. Only updated current user.`);
      return { 
        success: true, 
        message: friendsChanged ? 'Friend removed from your list, but could not update their list' : 'Friend was not in your list, but relationship cleanup attempted',
        partial: true
      };
    }
    
    // STEP 4: Try to update the friend's document
    try {
      const friendData = friendUserDoc.data();
      const friendFriends = friendData.friends || [];
      
      // Make sure current user exists in friend's list
      const currentUserInFriendsList = friendFriends.find(friend => friend.id === currentUserId);
      
      if (!currentUserInFriendsList) {
        console.warn(`Current user ${currentUserId} not found in friend's list. One-sided friendship?`);
        return { 
          success: true, 
          message: friendsChanged ? 'Friend removed from your list, but you were not in their list' : 'Friend relationship was already broken or one-sided',
          partial: false  // This is actually a complete success since both sides are now correct
        };
      }
      
      // Filter out the current user from friend's friends list
      const updatedFriendFriends = friendFriends.filter(
        friend => friend.id !== currentUserId
      );
      
      // STEP 4.1: Check for and remove any lingering friend requests in friend's document
      const friendFriendRequests = friendData.friendRequests || [];
      const friendSentRequests = friendData.sentFriendRequests || [];
      
      // Filter out any requests from the current user
      const updatedFriendFriendRequests = friendFriendRequests.filter(
        req => req.senderId !== currentUserId
      );
      
      // Filter out any requests to the current user
      const updatedFriendSentRequests = friendSentRequests.filter(
        req => req.receiverId !== currentUserId
      );
      
      // Update the friend's document with all changes
      await updateDoc(friendUserRef, {
        friends: updatedFriendFriends,
        friendCount: updatedFriendFriends.length,
        friendRequests: updatedFriendFriendRequests,
        sentFriendRequests: updatedFriendSentRequests
      });
      
      console.log(`Updated friend's document to remove current user: ${currentUserId}`);
      
      return { 
        success: true, 
        message: 'Friend relationship removed from both users',
        partial: false
      };
    } catch (friendUpdateError) {
      console.error(`Error updating friend's document:`, friendUpdateError);
      console.log('Friend update error message:', friendUpdateError.message);
      
      // Check if this is a permission error
      if (friendUpdateError.message && friendUpdateError.message.includes('permission')) {
        console.log('Permission error detected. This might be a security rules issue.');
      }
      
      // We still successfully updated the current user, so return partial success
      return { 
        success: true,
        message: 'Friend removed from your list, but could not update their list due to permissions',
        partial: true,
        error: friendUpdateError.message
      };
    }
  } catch (error) {
    console.error('Error in bidirectional friend removal:', error);
    return { success: false, message: `Error: ${error.message}` };
  }
};

// Sync Friend Removal - more robust implementation that ensures both sides are updated
export const syncRemoveFriend = async (currentUserId, friendId) => {
  try {
    if (!currentUserId || !friendId) {
      console.error('Missing user IDs for sync friend removal:', { currentUserId, friendId });
      return { success: false, message: 'Missing user IDs' };
    }

    console.log(`SYNC removing friend relationship between ${currentUserId} and ${friendId}`);
    
    // First remove the friend from the current user's document (always works)
    const currentResult = await bidirectionalFriendRemoval(currentUserId, friendId);
    
    if (!currentResult.success) {
      return currentResult; // Failed to update current user's document
    }
    
    // Check if the result was partial (couldn't update the friend's document)
    if (currentResult.partial) {
      console.log('First removal phase was partial, attempting to track for later sync...');
      
      try {
        // Add an entry to a special collection to track pending friend removals
        // This collection helps us track removals that need to be synced
        const pendingRemovalRef = doc(db, 'pendingFriendRemovals', `${currentUserId}_${friendId}`);
        
        await setDoc(pendingRemovalRef, {
          initiatorId: currentUserId,
          targetId: friendId,
          timestamp: new Date().toISOString(),
          attempts: 0,
          lastAttempt: null,
          createdAt: new Date().toISOString()
        });
        
        console.log('Created pending removal record for later sync');
        
        // Return success but note it's pending full sync
        return {
          success: true,
          message: 'Friend removed from your list and pending removal from their list',
          pendingSync: true
        };
      } catch (pendingError) {
        console.error('Failed to create pending removal record:', pendingError);
        
        // Even if we can't track the pending removal, the current user's side was successful
        return {
          success: true,
          message: 'Friend removed from your list, but could not track sync status',
          pendingSync: false
        };
      }
    }
    
    // If we got here, both sides were updated successfully
    return {
      success: true,
      message: 'Friend relationship fully removed from both users',
      pendingSync: false
    };
  } catch (error) {
    console.error('Error in sync friend removal:', error);
    
    // If there was an error but it's a permission error, try just removing from current user
    if (error.message && error.message.includes('permission')) {
      console.log('Permission error detected, trying simplified removal...');
      
      try {
        const simplifiedResult = await directFriendRemoval(currentUserId, friendId);
        if (simplifiedResult.success) {
          return {
            success: true,
            message: 'Friend removed from your list (simplified method)',
            partial: true
          };
        }
      } catch (simplifiedError) {
        console.error('Simplified removal also failed:', simplifiedError);
      }
    }
    
    return { success: false, message: `Error: ${error.message}` };
  }
};

// Debug function to check the current friendship status between two users
export const debugFriendshipStatus = async (user1Id, user2Id) => {
  try {
    console.log(`=== DEBUGGING FRIENDSHIP STATUS ===`);
    console.log(`User 1: ${user1Id}`);
    console.log(`User 2: ${user2Id}`);
    
    const [user1Doc, user2Doc] = await Promise.all([
      getDoc(doc(db, 'users', user1Id)),
      getDoc(doc(db, 'users', user2Id))
    ]);
    
    if (!user1Doc.exists()) {
      console.log(`User 1 (${user1Id}) document not found`);
      return { user1Exists: false };
    }
    
    if (!user2Doc.exists()) {
      console.log(`User 2 (${user2Id}) document not found`);
      return { user1Exists: true, user2Exists: false };
    }
    
    const user1Data = user1Doc.data();
    const user2Data = user2Doc.data();
    
    const user1Friends = user1Data.friends || [];
    const user2Friends = user2Data.friends || [];
    
    const user1HasUser2 = user1Friends.some(friend => friend.id === user2Id);
    const user2HasUser1 = user2Friends.some(friend => friend.id === user1Id);
    
    console.log(`User 1 has User 2 as friend: ${user1HasUser2}`);
    console.log(`User 2 has User 1 as friend: ${user2HasUser1}`);
    console.log(`User 1 total friends: ${user1Friends.length}`);
    console.log(`User 2 total friends: ${user2Friends.length}`);
    
    const status = {
      user1Exists: true,
      user2Exists: true,
      user1HasUser2,
      user2HasUser1,
      user1FriendCount: user1Friends.length,
      user2FriendCount: user2Friends.length,
      relationship: user1HasUser2 && user2HasUser1 ? 'mutual' : 
                   user1HasUser2 ? 'one-sided-1-to-2' :
                   user2HasUser1 ? 'one-sided-2-to-1' : 'none'
    };
    
    console.log('Friendship status:', status);
    console.log(`=== END DEBUGGING ===`);
    
    return status;
  } catch (error) {
    console.error('Error debugging friendship status:', error);
    return { error: error.message };
  }
};

// Check for pending friend removals that need to be synced
export const checkPendingRemovals = async (currentUserId) => {
  try {
    if (!currentUserId) return;
    
    console.log('Checking for pending friend removals to sync...');
    
    // Check both directions - where user is initiator or target
    const initiatorQuery = query(
      collection(db, 'pendingFriendRemovals'), 
      where('initiatorId', '==', currentUserId)
    );
    
    const targetQuery = query(
      collection(db, 'pendingFriendRemovals'), 
      where('targetId', '==', currentUserId)
    );
    
    const [initiatorResults, targetResults] = await Promise.all([
      getDocs(initiatorQuery),
      getDocs(targetQuery)
    ]);
    
    // Process where current user initiated the removal
    const initiatorPromises = [];
    initiatorResults.forEach(doc => {
      const data = doc.data();
      console.log(`Processing pending removal initiated by you to: ${data.targetId}`);
      
      // Try to remove again
      const removePromise = (async () => {
        try {
          // Update the attempt count
          await updateDoc(doc.ref, {
            attempts: (data.attempts || 0) + 1,
            lastAttempt: new Date().toISOString()
          });
          
          // Try to force removal on the target side
          // Since we're the initiator, we need to remove ourselves from their list
          const result = await directFriendRemoval(data.targetId, currentUserId);
          
          if (result.success) {
            console.log(`Successfully completed pending removal for ${data.targetId}`);
            await deleteDoc(doc.ref);
          }
        } catch (error) {
          console.error('Error processing pending removal:', error);
        }
      })();
      
      initiatorPromises.push(removePromise);
    });
    
    // Process where current user is the target of removal
    const targetPromises = [];
    targetResults.forEach(doc => {
      const data = doc.data();
      console.log(`Processing removal by ${data.initiatorId} that needs to be applied to your list`);
      
      const removePromise = (async () => {
        try {
          // Since we're the target, we can directly update our own document
          // to remove the initiator from our friends list
          const result = await directFriendRemoval(currentUserId, data.initiatorId);
          
          if (result.success) {
            console.log(`Successfully processed removal by ${data.initiatorId}`);
            await deleteDoc(doc.ref);
          }
        } catch (error) {
          console.error('Error processing target removal:', error);
        }
      })();
      
      targetPromises.push(removePromise);
    });
    
    // Wait for all operations to complete
    await Promise.all([...initiatorPromises, ...targetPromises]);
    
    const totalProcessed = initiatorPromises.length + targetPromises.length;
    if (totalProcessed > 0) {
      console.log(`Processed ${totalProcessed} pending friend removals`);
      return { processed: totalProcessed };
    }
    
    return { processed: 0 };
  } catch (error) {
    console.error('Error checking pending removals:', error);
    return { error: error.message };
  }
};